import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { StateReset } from 'ngxs-reset-plugin';
import { AlertController, NavController } from '@ionic/angular';

import { ModalService } from '../services/modal/modal.service';
import { CancelOrder } from 'src/app/store/self-service/self-service.action';
import {
  SskButton,
  SskConfirmModal
} from 'src/app/pages/self-service/modals/ssk-confirm-modal/ssk-confirm.modal';
import { PageConstant, SelfServiceConstant } from 'src/app/constants';
import {
  TICKETS_STATE_TOKEN,
  TicketsState
} from 'src/app/store/tickets/tickets.state';
import {
  TICKET_ITEMS_STATE_TOKEN,
  TicketItemsState
} from 'src/app/store/ticket-items/ticket-items.state';
import { MENU_STATE_TOKEN } from 'src/app/store/menu/menu.state.model';
import { StopIdle } from 'src/app/store/app/app.action';
import { DinersState } from 'src/app/store/diners/diners.state';
import { TransactionsState } from 'src/app/store/transactions/transactions.state';
import { SelfServiceState } from 'src/app/store/self-service/self-service.state';
import { TranslocoService } from '@ngneat/transloco';
import { categoryDisplayName } from 'src/app/utils';

@Injectable({
  providedIn: 'root'
})
export class SelfServiceProvider {
  constructor(
    private readonly store: Store,
    private readonly modalService: ModalService,
    private readonly navCtrl: NavController,
    private readonly alertCtrl: AlertController,
    private readonly translocoService: TranslocoService
  ) {}

  cancelOrder() {
    this.modalService.showModal<
      boolean,
      {
        description?: string;
        title?: string;
        yesBtn?: SskButton;
        noBtn?: SskButton;
      }
    >(
      SskConfirmModal,
      {
        title: this.translocoService.translate(
          'selfService.cancelOrderConfirmTitle'
        ),
        description: this.translocoService.translate(
          'selfService.cancelOrderConfirmDescription'
        ),
        yesBtn: {
          text: this.translocoService.translate(
            'selfService.cancelOrderButton'
          ),
          color: 'danger',
          fill: 'solid'
        },
        noBtn: {
          text: this.translocoService.translate(
            'selfService.cancelOrderNoButton'
          )
        }
      },
      { cssClass: 'dynamic-height-modal' },
      ({ data }) => {
        if (data) {
          this.store.dispatch(new CancelOrder());
        }
      }
    );
  }

  resetSelfService() {
    this.navCtrl.navigateRoot(PageConstant.SELF_SERVICE_SETUP_PAGE);
    this.store.dispatch(
      new StateReset(
        SelfServiceState,
        TicketsState,
        TicketItemsState,
        DinersState,
        TransactionsState
      )
    );
  }

  handleCancelOrderError(error: HttpErrorResponse): void {
    if (error.status === HttpStatusCode.NotFound) {
      this.store.dispatch(new StopIdle());

      this.alertCtrl
        .create({
          header: this.translocoService.translate('common.error'),
          backdropDismiss: false,
          message: this.translocoService.translate(
            'selfService.ticketNotFoundRedirect'
          ),
          buttons: [
            {
              text: this.translocoService.translate('common.ok'),
              handler: () => {
                this.resetSelfService();
              }
            }
          ]
        })
        .then((alert) => alert.present());
    }
  }

  isReachedMaxCategoryItem(categoryId: number): boolean {
    let isReachedMax = false;
    const { selectedTicket, tickets } =
      this.store.selectSnapshot(TICKETS_STATE_TOKEN);
    const ticketItemsById = this.store.selectSnapshot(TICKET_ITEMS_STATE_TOKEN)
      .ticketItems.byId;

    const selectedNormalizedTicket = tickets.byId[selectedTicket.data];
    const max_items = selectedNormalizedTicket.categories?.find(
      (i) => i.id === categoryId
    )?.max_items;
    const addedItemsAmount: number = (
      selectedNormalizedTicket.ticket_items || []
    ).reduce((acc, ticketItemUuid) => {
      const ticketItem = ticketItemsById[ticketItemUuid];

      if (
        ticketItem &&
        !ticketItem.parent_uuid &&
        ticketItem.category_id === categoryId
      ) {
        acc++;
      }

      return acc;
    }, 0);

    isReachedMax =
      typeof max_items === 'number' && addedItemsAmount >= max_items;

    if (isReachedMax) {
      const category = this.store
        .selectSnapshot(MENU_STATE_TOKEN)
        .menu.categories.find((x) => x.id === categoryId);
      const categoryName = category
        ? categoryDisplayName(category, this.translocoService.getActiveLang())
        : 'N/A';

      const headerText = this.translocoService.translate(
        'selfService.reachedMaxCategoryHeader',
        { categoryName }
      );
      const messageText = this.translocoService.translate(
        'selfService.reachedMaxCategoryMessage'
      );
      const buttonText = this.translocoService.translate('common.ok');

      this.modalService.alert(
        SelfServiceConstant.REACHED_MAX_ALERT_OPTS(
          headerText,
          messageText,
          buttonText
        )
      );
    }
    return isReachedMax;
  }
}
